import React, { useEffect, useState } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepButton,
  Paper,
  Typography,
  Alert,
} from '@mui/material';
import type { ExecuteEntity, PlanEntity, RunEntity } from '@/types/run-stage.types';
import { ExecutionModeSelector } from './ExecutionModeSelector';
import { ExecuteStepRunner } from './ExecuteStepRunner';
import { ExecutionSummary } from './ExecutionSummary';

interface ExecuteStageProps {
  plan: PlanEntity;
  execute: ExecuteEntity;
  run: RunEntity;
  isReadOnly?: boolean;
}

const STEPS = ['Execution Mode', 'Execute Steps', 'Execution Summary'];

/**
 * Determine starting step based on current state:
 * - If execution mode not set → step 0
 * - If run completed → step 2 (summary)
 * - If mode set & not completed → step 1 (execution)
 */
function deriveInitialStep(plan: PlanEntity, execute: ExecuteEntity): number {
  if (!plan.typeOfExecution) return 0;
  if (execute.completed) return 2;
  return 1;
}

export const ExecuteStage: React.FC<ExecuteStageProps> = ({
  plan,
  execute,
  run,
  isReadOnly = false,
}) => {
  const [activeStep, setActiveStep] = useState(() => deriveInitialStep(plan, execute));

  // When plan/execute change externally (e.g. after refresh), update step if needed
  useEffect(() => {
    setActiveStep(deriveInitialStep(plan, execute));
  }, [plan, execute]);

  const executionMode = (plan.typeOfExecution?.charAt(0).toUpperCase() +
    (plan.typeOfExecution?.slice(1).toLowerCase() ?? '')) as 'Online' | 'Offline' | '';
  const isOffline = executionMode === 'Offline';

  const handleExecutionStarted = (mode: 'Online' | 'Offline') => {
    // After mode is selected and APIs called, advance to step 1
    setActiveStep(1);
  };

  const handleRunFinished = () => {
    // After run is finished, advance to summary step
    setActiveStep(2);
  };

  const handleStepClick = (index: number) => {
    // Don't allow going back to step 0 if mode already selected
    if (index === 0 && plan.typeOfExecution) return;
    // Don't allow skipping to step 2 unless execution is complete
    if (index === 2 && !execute.completed) return;
    setActiveStep(index);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Stepper nav */}
      <Paper variant="outlined" sx={{ px: 3, py: 1.5, mb: 2, flexShrink: 0 }}>
        <Stepper nonLinear activeStep={activeStep}>
          {STEPS.map((label, index) => {
            const disabled =
              (index === 0 && !!plan.typeOfExecution) ||
              (index === 2 && !execute.completed);
            return (
              <Step key={label} completed={index < activeStep}>
                <StepButton onClick={() => handleStepClick(index)} disabled={disabled}>
                  <Typography variant="body2">{label}</Typography>
                </StepButton>
              </Step>
            );
          })}
        </Stepper>
      </Paper>

      {/* Step content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {activeStep === 0 && (
          <ExecutionModeSelector
            plan={plan}
            run={run}
            isReadOnly={isReadOnly}
            onExecutionStarted={handleExecutionStarted}
          />
        )}
        {activeStep === 1 && (
          <ExecuteStepRunner
            execute={execute}
            plan={plan}
            isReadOnly={isReadOnly}
            isOffline={isOffline}
            onRunFinished={handleRunFinished}
          />
        )}
        {activeStep === 2 && (
          <ExecutionSummary execute={execute} />
        )}
      </Box>
    </Box>
  );
};
